import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";

import ModalMotion, {
  AnimateDirection,
} from "@/components/Molecules/ModalMotion";
import FormInputs, { SuccessModal } from "./FormInputs";
import useRegisterForm from "./useRegisterForm";
import usePackages from "./usePackages";
import usePaymentForm from "./usePaymentForm";

import { timezone as tz } from "@/utils";

import { useAppDispatch, useAppSelector } from "@/hooks";
import * as actions from "@/redux/LandingPageReducer";

import { StrikeThroughText } from "./style";

const PartnerForm = ({
  pageType,
  isYogaPage,
  isNutritionPage,
  isHealthPage,
  isDermaPage,
  isPartnershipPage,
  // Open Form in Modal
  isCallbackModal,
  setShowCallbackModal,
  //
  urlParams,
  urlsArguments: _urlsArguments,
  // Default Data
  defaultPackageSlug,
  defaultTimezone,
  // Pricing Data
  discount,
  price,
  priceConstant,
  //
  secondaryCTA,
  //
  options = [],
  // Show Fields
  showNameField = true,
  showCVIDField,
  showPhoneField = true,
  showEmailField,
  showCompanyNameField,
  showGoalField = true,
  showSelectPackage,
  showPromoCodeField,
  showReferralField,
  showDermaTypeField,
  showSecondaryCTA,
  // Success Modal Content
  showAppDownLoadButtonLink,
  callbackSuccessTitle,
  callbackSuccessSubtitle,
  //
  isRequestCallbackPage,
  defaultSelectedPackDuration,
  defaultSelectedPack,
  defaultSelectedGoal,
  isFreeTrialPage,
  //
  packagePageType,
  //
  paymentSuccessPath,
  ...rest
}) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { countryCodeData } = useAppSelector((state) => state.dashboard);

  const [isLoading, setIsLoading] = useState(false);
  const [leadData, setLeadData] = useState(null);

  const timezone = defaultTimezone || tz;
  const isPackageSelectedByDefault =
    defaultSelectedPackDuration || defaultSelectedPack;

  const urlsArguments = _urlsArguments || urlParams;

  const fields = useMemo(
    () => ({
      ...(showNameField && { name: "" }),
      ...(showCVIDField && { cvid: "" }),
      ...(showPhoneField && {
        countryCode: countryCodeData?.default?.code || "+91",
        mobile: "",
      }),
      ...(showEmailField && { email: "" }),
      ...(showCompanyNameField && { companyName: "" }),
      ...(showGoalField && { goal: defaultSelectedGoal || "" }),
      ...(showSelectPackage && { selectedPackage: "" }),
      ...(showReferralField && { referral: "" }),
      ...(showPromoCodeField && { promoCode: "" }),
      ...(showDermaTypeField && { dermaType: "" }),
    }),
    [
      showNameField,
      showCVIDField,
      showPhoneField,
      showEmailField,
      showCompanyNameField,
      showGoalField,
      showSelectPackage,
      showReferralField,
      showPromoCodeField,
      showDermaTypeField,
      defaultSelectedGoal,
      countryCodeData,
    ]
  );

  // STATE MANAGEMENT:
  const {
    inputs,
    showSuccessModal,
    isLoading: isThisPageLoading,
    handleBlur,
    handleChange,
    handleSelectChange,
    onSubmitCallback,
    onShowCallbackModal,
    onHideCallbackModal,
    validateInput,
    promoCodeButtonText,
    applyPromoCode,
    isPromoCodeApplied,
    isPromoCodeChecking,
    promoCodeData,
    removeAppliedPromoCode,
  } = useRegisterForm(fields);

  const {
    name,
    cvid,
    countryCode,
    mobile,
    email,
    companyName,
    goal,
    selectedPackage,
    referral,
    promoCode,
    promoCodeError,
    dermaType,
  } = inputs;

  // Selected Goal ID
  const goalId = goal && options?.find((option) => option.value === goal)?.id;

  // Goal Slug with Fallback
  const goalSlug = goal || defaultPackageSlug;

  // Selected Package Slug
  let packSlug = goalSlug;
  // if Yoga Page then use selectedPackage as packSlug
  if (isYogaPage && selectedPackage) packSlug = selectedPackage;

  // GET PACKAGES DATA if (showSelectPackage === true):
  const {
    isPackageFetching,
    packageOptions: packOptions,
    selectedPack,
    priceObj,
  } = usePackages({
    showSelectPackage,
    pageType,
    price,
    selectedPackage,
    defaultSelectedPackDuration,
    defaultSelectedPack,
    discount,
    goalId,
    timezone,
    isPartnershipPage,
  });

  let packageOptions = packOptions;

  const _packageOptions = useMemo(
    () =>
      packOptions?.filter((pac) =>
        [
          "nutrition-monthly-pack",
          "nutrition-quarterly-pack",
          "nutrition-half-yearly-pack",
          "nutrition-yearly-pack",
        ].includes(pac?.value)
      ),
    [packOptions]
  );

  //TODO: Remove this Hardcoded Filters
  if (isNutritionPage) {
    packageOptions = _packageOptions;
  }

  // Purchase Plan Slug
  let purchasePlanSlug =
    selectedPackage &&
    packageOptions?.find((p) => p.value === selectedPackage)?.slug;
  if (selectedPackage && defaultSelectedPackDuration) {
    purchasePlanSlug = packageOptions?.find(
      (p) => p.duration === defaultSelectedPackDuration
    )?.slug;
  } else if (defaultSelectedPack) {
    purchasePlanSlug = defaultSelectedPack?.slug;
  }

  const applyCode = () => {
    const isValidInputs = validateInput(inputs);
    const apiData = {
      coupon_code: promoCode?.toUpperCase(),
      slug: goalSlug,
      mobile: mobile,
      phone_prefix: countryCode,
      is_hero_package: true,
      ...((showSelectPackage || isPackageSelectedByDefault) && {
        package_slug: purchasePlanSlug,
      }),
      ...(goalId && { nutrition_goal_id: goalId }),
      timezone,
      ...(isPartnershipPage && { page_type: pageType }),
      ...(packagePageType && { page_type: packagePageType }),
    };
    applyPromoCode(isValidInputs, apiData);
  };

  const applyPromoCodeHandler = isPromoCodeApplied
    ? removeAppliedPromoCode
    : applyCode;

  // HANDLE PAYMENT
  const { openPaymentPage } = usePaymentForm({ paymentSuccessPath });

  // CAPTURE LEAD
  const onBlur = () => {
    const apiData = {
      is_lead: true,
      ...(showNameField && { name }),
      ...(showCVIDField && { club_vistara_id: cvid }),
      ...(showPhoneField && { phone_prefix: countryCode, mobile }),
      ...(showEmailField && { email }),
      ...(showCompanyNameField && { companyName }),
      ...((showGoalField || defaultPackageSlug) && {
        package_slug: packSlug,
      }),
      ...(showReferralField && { referral }),
      ...(showDermaTypeField && { derma_type: dermaType }),
      urls_arguments: urlsArguments,
      timezone,
      ...(isPartnershipPage && { page_type: pageType }),
      ...(packagePageType && { page_type: packagePageType }),
    };

    dispatch(actions.saveLead({ data: apiData }));
  };

  const resetPromoCodeErrorsOnSubmit = () => {
    if (!isPromoCodeApplied && (promoCode || promoCodeError))
      removeAppliedPromoCode();
  };

  const submitCallback = () => {
    let apiData = {
      is_callback_request: true,
      ...(showNameField && { name }),
      ...(showCVIDField && { club_vistara_id: cvid }),
      ...(showPhoneField && { phone_prefix: countryCode, mobile }),
      ...(showEmailField && { email }),
      ...(showCompanyNameField && { company_name: companyName }),
      ...((showGoalField || defaultPackageSlug) && {
        package_slug: packSlug,
      }),
      ...(showReferralField && { referral_code: referral }),
      ...(showDermaTypeField && { derma_type: dermaType }),
      urls_arguments: urlsArguments,
      timezone,
      ...(isPartnershipPage && { page_type: pageType }),
      ...(packagePageType && { page_type: packagePageType }),
    };
    setIsLoading(true);
    dispatch(actions.saveLead({ data: apiData }))
      .then((res) => {
        setIsLoading(false);
        setLeadData({ leadId: res?.payload?.lead_id, healthGoal: packSlug });
        onShowCallbackModal();
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const submitFreeTrial = () => {
    const coupon_code = (promoCode || "")?.toUpperCase();
    const apiData = {
      is_free_trial_requested: true,
      ...(showNameField && { name }),
      ...(showCVIDField && { club_vistara_id: cvid }),
      ...(showPhoneField && { phone_prefix: countryCode, mobile }),
      ...(showEmailField && { email }),
      ...(showCompanyNameField && { companyName }),
      ...((showGoalField || defaultPackageSlug) && { package_slug: goalSlug }),
      ...((showSelectPackage || isPackageSelectedByDefault) && {
        purchased_plan_slug: purchasePlanSlug,
      }),
      ...(showReferralField && { referral_code: referral }),
      ...(showDermaTypeField && { derma_type: dermaType }),
      ...(coupon_code && { coupon_code: coupon_code }),
      urls_arguments: urlsArguments,
      transaction_id: "",
      payment_mode: "",
      timezone,
      is_callback_request: false,
      ...(isPartnershipPage && { page_type: pageType }),
      ...(packagePageType && { page_type: packagePageType }),
    };
    dispatch(actions.postPackageSave({ router, data: apiData, paymentSuccessPath }));
  };

  const submitPackagePurchase = () => {
    const _inputs = {
      ...inputs,
      promoCode: isPromoCodeApplied && promoCode,
      goal: goalSlug,
      ...((showSelectPackage || isPackageSelectedByDefault) && {
        purchasedPlanSlug: purchasePlanSlug,
      }),
      isPartnershipPackagePage: showSelectPackage || isPackageSelectedByDefault,
      urlsArguments,
      slug: goalSlug,
      ...(isPartnershipPage && { pageType }),
      ...(packagePageType && { pageType: packagePageType }),
    };

    const _priceObj = {
      ...priceObj,
      price: isPromoCodeApplied ? promoCodeData.new_price : priceObj?.price,
    };
    openPaymentPage(_inputs, _priceObj);
  };

  const onSubmit = (isCallbackRequested) => () => {
    resetPromoCodeErrorsOnSubmit();
    if (isCallbackRequested) {
      submitCallback();
    } else {
      const isNewPriceZero =
        isPromoCodeApplied && promoCodeData.new_price === 0;
      if (isFreeTrialPage || isNewPriceZero) {
        submitFreeTrial();
      } else {
        submitPackagePurchase();
      }
    }
  };

  const onSubmitButtonClick = (isCallbackRequested) => {
    const _isCallbackRequested =
      isCallbackRequested ||
      isRequestCallbackPage ||
      price?.price === "callback";
    onSubmitCallback(inputs, onSubmit(_isCallbackRequested));
  };

  // INPUT HANDLER
  const onInputChange = (e) => handleChange(e);
  const onSelectChange = (name, value) => handleSelectChange(name, value);
  const onInputBlur = (e) => handleBlur(e, onBlur);

  //
  useEffect(() => {
    if (defaultSelectedPackDuration) {
      const pack = packageOptions?.find(
        (p) => p?.duration === defaultSelectedPackDuration
      );
      onSelectChange("selectedPackage", pack?.value);
    } else {
      onSelectChange(
        "selectedPackage",
        defaultSelectedPack?.slug || defaultSelectedPack?.package_slug
      );
    }
  }, [defaultSelectedPackDuration, defaultSelectedPack, packageOptions]);

  const productTypeMap = {
    YOGA_PARTNERSHIP: "Yoga",
    NUTRITION_PARTNERSHIP: "Nutrition",
    HEALTH_PARTNERSHIP: "Health",
    DERMA_PARTNERSHIP: "Dermatology",
  };

  const productType = productTypeMap[pageType];

  // Primary CTA
  const generatePrimaryCTA = () => {
    if (isRequestCallbackPage) {
      return "Request a callback";
    } else if (isFreeTrialPage) {
      return "Book a Free Trial";
    } else if (showSelectPackage) {
      if (selectedPack) {
        let packLabel = `${selectedPack?.title} ${productType} Plan `;
        if (defaultSelectedPack) packLabel = "";

        if (isPromoCodeApplied) {
          return (
            <>
              {`Buy ${packLabel}for ${priceObj?.currency} `}
              <StrikeThroughText>
                {promoCodeData?.original_price}
              </StrikeThroughText>
              {` ${promoCodeData?.new_price}`}
            </>
          );
        } else {
          return (
            <>
              {`Buy ${packLabel}for ${priceObj?.currency} `}
              {priceObj?.original_price != priceObj?.price && (
                <StrikeThroughText>
                  {priceObj?.original_price}
                </StrikeThroughText>
              )}
              {` ${priceObj?.price}`}
            </>
          );
        }
      } else if (defaultSelectedPackDuration) {
        return `Buy ${defaultSelectedPackDuration} Month ${productType} Plan`;
      } else {
        return `Buy ${productType} Plan`;
      }
    } else if (priceConstant) {
      if (isPromoCodeApplied && promoCodeData) {
        return (
          <>
            {`${priceConstant} `}
            <StrikeThroughText>
              {promoCodeData?.original_price}
            </StrikeThroughText>
            {` ${promoCodeData?.new_price}`}
          </>
        );
      } else if (priceObj?.price) {
        return `${priceConstant} ${priceObj?.price}`;
      }
    }
    return "Request a callback";
  };

  // MODAL
  const onHide = useCallback(() => {
    // dispatch(resetNutritionCallbackValues())
  }, [dispatch]);

  const onHideNutritionCallbackModal = useCallback(() => {
    if (isCallbackModal) setShowCallbackModal(false);
    onHideCallbackModal(onHide);
  }, [isCallbackModal, setShowCallbackModal, onHide, onHideCallbackModal]);

  useEffect(() => {
    if (showSuccessModal === true) {
      setTimeout(
        onHideNutritionCallbackModal,
        showAppDownLoadButtonLink ? 60000 : 6000
      );
    }
  }, [
    showSuccessModal,
    onHideNutritionCallbackModal,
    showAppDownLoadButtonLink,
  ]);

  const _isLoading =
    (isLoading && isThisPageLoading) ||
    (isPackageSelectedByDefault && isPackageFetching); //TODO: Fix `isThisPageLoading  logic

  const FormInputsComp = (
    <FormInputs
      // State
      {...inputs}
      promoCode={promoCode}
      promoCodeError={promoCodeError}
      isLoading={_isLoading}
      isPackageFetching={isPackageFetching}
      isPromoCodeChecking={isPromoCodeChecking}
      isPromoCodeApplied={isPromoCodeApplied}
      promoCodeData={promoCodeData}
      // Actions
      handleChange={onInputChange}
      handleSelectChange={onSelectChange}
      handleBlur={onInputBlur}
      onSubmitCallback={onSubmitButtonClick}
      applyPromoCode={applyPromoCodeHandler}
      //
      goalOptions={options}
      packageOptions={packageOptions}
      // Show Fields
      showLabels={false}
      showNameField={showNameField}
      showCVIDField={showCVIDField}
      showPhoneField={showPhoneField}
      showEmailField={showEmailField}
      showCompanyNameField={showCompanyNameField}
      showGoalField={showGoalField && !defaultSelectedGoal}
      showDermaTypeField={showDermaTypeField}
      showSelectPackage={showSelectPackage && !isPackageSelectedByDefault}
      showPromoCodeField={showPromoCodeField}
      showReferralField={showReferralField}
      showSecondaryCTA={showSecondaryCTA}
      //
      primaryCTA={generatePrimaryCTA()}
      secondaryCTA={secondaryCTA}
      promoCodeButtonText={promoCodeButtonText}
      //
      {...rest}
    />
  );

  const SuccessModalComp = (
    <SuccessModal
      showButton={false}
      showAppDownLoadButtonLink={showAppDownLoadButtonLink}
      callbackSuccessTitle={callbackSuccessTitle}
      callbackSuccessSubtitle={callbackSuccessSubtitle}
      onHideNutritionCallbackModal={onHideNutritionCallbackModal}
      leadData={leadData}
    />
  );

  const component = showSuccessModal ? SuccessModalComp : FormInputsComp;

  if (isCallbackModal) {
    return (
      <ModalMotion
        overlayClosable
        onClose={onHideNutritionCallbackModal}
        width="582px"
        animateDirection={AnimateDirection.CENTER}
        borderRadius="10px"
        showClose
        iconTopPosition="20px"
        iconRightPosition="20px"
      >
        {component}
      </ModalMotion>
    );
  }

  return component;
};

export default PartnerForm;
